import {
  FieldFilterPicker,
  Override,
  ArrayHelper,
  DateHelper,
  Duration,
  CollectionFilter
} from "@bryntum/schedulerpro";

// Array field type is not supported for filtering yet, but we have a custom converter therefore we can use same
// operators as string fields
const DEFUALT_STRING_OPERATORS = FieldFilterPicker.defaultOperators.string.slice();
const isInludedIn = DEFUALT_STRING_OPERATORS.find((operator) => operator.value === "isIncludedIn");
FieldFilterPicker.defaultOperators.array = DEFUALT_STRING_OPERATORS;
// Created settings field type for settings (teams, category, status, tags) field
FieldFilterPicker.defaultOperators._swivu_settings = DEFUALT_STRING_OPERATORS.filter((operator) => operator.value !== "isIncludedIn")
// Moving `isIncludedIn` operator to top in settings fields
FieldFilterPicker.defaultOperators._swivu_settings.unshift(isInludedIn);
// Only allow 3 operators for time - empty, not empty and contains. More complex matcher is difficult to set up. See this ticket:
// https://github.com/bryntum/support/issues/7664
FieldFilterPicker.defaultOperators._swivu_time = DEFUALT_STRING_OPERATORS.filter(f => f.value.match(/(includes|mpty)/));

Override.apply(
  class {
    static target = {
      class: FieldFilterPicker
    };

    // by default for picker value that offer list of options to choose from, (such as oneOf and similar), they also
    // allow creating value on the fly if not already exists in a list of options to choose from.
    // following override will prevent that behavior
    getValueFieldConfigs() {
      const config = this._overridden.getValueFieldConfigs.call(this);
      if (this.isMultiSelectValueField) {
        if (Array.isArray(config)) {
          config.forEach((config) => {
            config.createOnUnmatched = false;
          })
        }
      }
      return config;
    }

    getUniqueDataValues(extraValuesToInclude = []) {
      const me = this,
        { fieldType } = me,
        { relatedDisplayField } = me.selectedField;

      if (!me.store || !me._filter?.property) {
        return [];
      }

      let values, sortedValues;

      if (me.fieldIsRelation) {
        const { foreignStore } = me.currentPropertyRelationConfig;

        if (relatedDisplayField) {
          values = foreignStore.allRecords.reduce((options, record) => {
            if (record.id != null) {
              options.push({
                text: record.getValue(relatedDisplayField),
                value: record.id
              });
            }
            return options;
          }, []);
          sortedValues = values.sort((a, b) => me.sortStrings(a.text, b.text));
        } else {
          values = foreignStore.allRecords.map((record) => record.id);
        }
      } else {
        values = me.store.allRecords.map((record) =>
          record.getValue(me._filter.property)
        );
      }

      if (!sortedValues) {
        values.push(...extraValuesToInclude);

        const uniqueValues = ArrayHelper.unique(
          values.reduce((primitiveValues, value) => {
            if (value != null && String(value).trim() !== "") {
              if (fieldType === "date") {
                primitiveValues.push(value.valueOf());
              } else if (fieldType === "duration") {
                primitiveValues.push(value.toString());
              } else {
                // PATCHED LINE
                // This is only change in the large fn meant to support looking for unique values in an array of arrays
                if (Array.isArray(value)) {
                  primitiveValues.push(...value.map(r => r.valueOf()));
                } else {
                  primitiveValues.push(value);
                }
              }
            }
            return primitiveValues;
          }, [])
        );

        if (fieldType === "string") {
          sortedValues = uniqueValues.sort(me.sortStrings);
        } else if (fieldType === "duration") {
          sortedValues = uniqueValues
            .map((durationStr) => new Duration(durationStr))
            .filter((duration) => duration.isValid)
            .sort(me.sortDurations);
        } else {
          sortedValues = uniqueValues.sort(me.sortNumerics);
        }

        if (fieldType === "date") {
          sortedValues = sortedValues.map((timestamp) => {
            const date = new Date(timestamp);

            return {
              text: DateHelper.format(date, me.dateFormat),
              value: timestamp
            };
          });
        } else if (fieldType === "duration") {
          sortedValues = sortedValues.map((duration) => duration.toString());
        }
      }
      return sortedValues;
    }

    // we don't need case-sensitive checkbox
    refreshCaseSensitive() {
      const me = this;
      this._overridden.refreshCaseSensitive.call(this);

      const { caseSensitive } = me.widgetMap;
      caseSensitive.hidden = true;
    }
  }
);

Override.apply(
  class {
    static target = {
      class: CollectionFilter
    };

    isIncludedIn(v) {
      if (this.filterValue.length === 0) {
        return true;
      }
      // Allows to look if array elements are contained in another array
      else if (Array.isArray(v)) {
        // when we persist `caseSensitive` in view state with value false, then internally CollectionFilter class bryntum try to convert all values of `filterValue`
        // to lowercase even if the caseSensitive check is set to false. So in order to temporarily fix that I made the value compare against to lowercase as well.
        return v.some((a) => this.filterValue.includes((a || "").toLowerCase()));
      } else {
        return this.filterValue.includes(v);
      }
    }

    // In the newest version 6.1.7+ the class cleans up the filters and keeping only properties that are documented
    // cause of this the hidden property for instance from the archived filters gets removed and view state always gets dismissed
    // For now, patched the state getter function to add the hidden property, but it would be better to find a reliable solution to this
    getState() {
      const state = this._overridden.getState.call(this);
      if (this.hidden) state.hidden = this.hidden;
      return state;
    }
  }
);
